import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import AddLinkIcon from '@mui/icons-material/AddLink'
import services from '../../services'
import { useAuth } from '../../hooks/useAuth'
import { useNotification } from '../../hooks/useNotification.js'
import { atualizarMarcas, useNovoPortal } from '../estado'
import { TRADE_TALENTOS } from '../tema'
import Janela from '../componentes/Janela'
import VinculoDialog, { ItemVinculo } from '../componentes/VinculoDialog'
import { plural } from './comum'
import GruposDoMenu from './GruposDoMenu'
import './gestao.scss'

interface AmbienteGestao { id: number; nome: string; marcaId?: number | null; dashboards: number; grupos: number; ativo: boolean }
interface DashboardNoAmbiente { id: number; nome: string; tipo: 'powerbi' | 'html'; ativo: boolean; regra?: string | null; filtros: number; equipes: number; pessoas: number; oneflow: boolean }
interface GrupoGestao { id: number; nome: string; ordem: number; dashboards: DashboardNoAmbiente[] }
interface AmbienteDetalhe { id: number; nome: string; marcaId?: number | null; grupos: GrupoGestao[]; ativo: boolean }

/** Gestão › Ambientes: cada ambiente com os dashboards que mostra, e o que é dele (grupo, regra, filtros, acessos, oneflow). */
export default function Ambientes() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { notify } = useNotification()
  const { getUserRoles } = useAuth()
  const roles: string[] = getUserRoles() || []
  const admin = roles.includes('bi_dashboards_read_any')
  const est = useNovoPortal()

  const [lista, setLista] = useState<AmbienteGestao[] | null>(null)
  const [erroLista, setErroLista] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  const [detalhe, setDetalhe] = useState<AmbienteDetalhe | null>(null)
  const [erroDetalhe, setErroDetalhe] = useState<string | null>(null)
  const [buscaDash, setBuscaDash] = useState('')
  const [situacaoDash, setSituacaoDash] = useState('ativo')
  const [novo, setNovo] = useState(false)
  const [renomear, setRenomear] = useState(false)
  const [nome, setNome] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erroJanela, setErroJanela] = useState<string | null>(null)
  const [adicionar, setAdicionar] = useState(false)
  const [catalogo, setCatalogo] = useState<{ id: number; name: string; type?: string; active: boolean }[] | null>(null)
  const [grupoNovo, setGrupoNovo] = useState('')
  const [situacaoAmb, setSituacaoAmb] = useState('ativo')
  const [inativar, setInativar] = useState(false)

  const atual = Number(id) || null

  const carregarLista = useCallback(async () => {
    const r = await services.api.novoPortalGestaoAmbientes()
    if (r.fail) setErroLista(r.error || 'Não foi possível carregar os ambientes.')
    else setLista(r.data || [])
  }, [])

  const carregarDetalhe = useCallback(async () => {
    setErroDetalhe(null)
    if (!atual) { setDetalhe(null); return }
    const r = await services.api.novoPortalGestaoAmbiente(atual)
    if (r.fail) { setDetalhe(null); setErroDetalhe(r.error || 'Não foi possível abrir o ambiente.') } else setDetalhe(r.data)
  }, [atual])

  useEffect(() => { carregarLista() }, [carregarLista])
  useEffect(() => { setBuscaDash(''); carregarDetalhe() }, [carregarDetalhe])

  // Sem ambiente na rota: abre o primeiro da lista (no celular, fica a lista).
  useEffect(() => {
    if (!atual && lista && lista.length && window.innerWidth > 768) navigate(`/app/gestao/ambientes/${lista[0].id}`, { replace: true })
  }, [atual, lista, navigate])

  const nomeDaMarca = (marcaId?: number | null) => (marcaId && est.marcas.find(m => m.id === marcaId)?.nome) || TRADE_TALENTOS.nome
  const q = busca.trim().toLowerCase()
  const visiveis = (lista || [])
    .filter(a => !situacaoAmb || (situacaoAmb === 'ativo') === a.ativo)
    .filter(a => !q || a.nome.toLowerCase().includes(q) || nomeDaMarca(a.marcaId).toLowerCase().includes(q))

  const qd = buscaDash.trim().toLowerCase()
  const grupos = useMemo(() => (detalhe?.grupos || []).map(g => ({
    ...g,
    dashboards: g.dashboards
      .filter(d => !situacaoDash || (situacaoDash === 'ativo') === d.ativo)
      .filter(d => !qd || d.nome.toLowerCase().includes(qd) || g.nome.toLowerCase().includes(qd))
  })), [detalhe, qd, situacaoDash])
  const visiveisDash = grupos.reduce((s, g) => s + g.dashboards.length, 0)
  const totalDash = (detalhe?.grupos || []).reduce((s, g) => s + g.dashboards.length, 0)

  const criar = async () => {
    setSalvando(true); setErroJanela(null)
    const r = await services.api.criarEnvironment(nome.trim())
    setSalvando(false)
    if (r.fail) { setErroJanela(r.error || 'Não foi possível criar o ambiente.'); return }
    setNovo(false)
    notify(`Ambiente ${nome.trim()} criado.`, { variant: 'success' })
    await carregarLista()
    if (r.data?.id) navigate(`/app/gestao/ambientes/${r.data.id}`)
  }

  const gravarNome = async () => {
    if (!detalhe) return
    setSalvando(true); setErroJanela(null)
    const r = await services.api.editarEnvironment(nome.trim(), detalhe.id)
    setSalvando(false)
    if (r.fail) { setErroJanela(r.error || 'Não foi possível renomear.'); return }
    setRenomear(false)
    notify('Nome alterado.', { variant: 'success' })
    carregarLista(); carregarDetalhe()
  }

  const trocarMarca = async (valor: string) => {
    if (!detalhe) return
    const marcaId = valor ? Number(valor) : null
    const r = await services.api.novoPortalMarcaDoAmbiente(detalhe.id, marcaId)
    if (r.fail) { notify(r.error || 'Não foi possível trocar a marca.', { variant: 'error' }); return }
    notify(`${detalhe.nome} agora usa a marca ${nomeDaMarca(marcaId)}.`, { variant: 'success' })
    setDetalhe({ ...detalhe, marcaId })
    setLista(l => (l || []).map(a => (a.id === detalhe.id ? { ...a, marcaId } : a)))
    atualizarMarcas(est.marcas, est.ambientes.map(a => (a.id === detalhe.id ? { ...a, brandId: marcaId } : a)))
  }

  const abrirAdicionar = () => {
    setGrupoNovo(detalhe?.grupos[0]?.nome || '')
    setAdicionar(true)
    if (!catalogo) {
      services.api.novoPortalCatalogo().then((r: any) => setCatalogo(r.fail ? [] : (r.data || []).map((d: any) => ({ id: d.id, name: d.nome, type: d.tipo, active: d.ativo }))))
    }
  }

  // Inativar não apaga: o ambiente some do portal de quem usa, com tudo guardado para voltar.
  const confirmarAtivo = async () => {
    if (!detalhe) return
    setSalvando(true)
    const r = await services.api.novoPortalAtivarAmbiente(detalhe.id, !detalhe.ativo)
    setSalvando(false)
    if (r.fail) { notify(r.error || 'Não foi possível mudar a situação.', { variant: 'error' }); return }
    notify(detalhe.ativo ? `${detalhe.nome} inativado: some do portal de quem usa. Nada foi apagado.` : `${detalhe.nome} reativado.`, { variant: 'success' })
    setInativar(false)
    carregarLista(); carregarDetalhe()
  }

  const jaNoAmbiente = new Set((detalhe?.grupos || []).flatMap(g => g.dashboards.map(d => d.id)))
  const itensCatalogo: ItemVinculo[] = (catalogo || []).filter(d => d.active).map(d => ({
    id: String(d.id), nome: d.name, etiqueta: d.type === 'html' ? 'HTML' : undefined, tipo: d.type === 'html' ? 'html' : 'powerbi',
    ja: jaNoAmbiente.has(d.id) ? 'Já aparece' : undefined
  }))

  return (
    <div className="ng">
      <div className="ng-cab">
        <h1>Ambientes</h1>
        <div className="ng-acoes">
          {roles.includes('bi_environments_create') && <button type="button" className="ng-btn prim" onClick={() => { setNome(''); setErroJanela(null); setNovo(true) }}><AddIcon />Novo ambiente</button>}
        </div>
      </div>
      <p className="ng-explic">Cada ambiente tem os próprios grupos no menu e, para cada dashboard, a regra, os filtros, quem acessa e o oneflow.</p>

      <div className={`ng-ambientes${atual ? ' com-detalhe' : ''}`}>
        <aside className="ng-cartao ng-amb-lista" aria-label="Ambientes">
          <label className="ng-busca"><SearchIcon fontSize="small" />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar ambiente ou marca" aria-label="Buscar ambiente ou marca" />
          </label>
          <select className="ng-select" value={situacaoAmb} onChange={e => setSituacaoAmb(e.target.value)} aria-label="Situação do ambiente">
            <option value="ativo">Só ativos</option><option value="inativo">Só inativos</option><option value="">Ativos e inativos</option>
          </select>
          <div className="ng-amb-itens">
            {erroLista && <p className="ng-explic">{erroLista}</p>}
            {!lista && !erroLista && <p className="ng-explic">Carregando…</p>}
            {lista && visiveis.length === 0 && <p className="ng-explic">Nenhum ambiente com esse nome.</p>}
            {visiveis.map(a => (
              <button key={a.id} type="button" className="ng-amb-item" aria-current={a.id === atual ? 'page' : undefined} onClick={() => navigate(`/app/gestao/ambientes/${a.id}`)}>
                <span className="ng-amb-ini">{a.nome.charAt(0)}</span>
                <span className="ng-amb-txt"><b>{a.nome} {!a.ativo && <span className="ng-pill inativo">Inativo</span>}</b><small>{nomeDaMarca(a.marcaId)} · {plural(a.dashboards, 'dashboard', 'dashboards')}</small></span>
              </button>
            ))}
          </div>
        </aside>

        <section className="ng-amb-detalhe">
          {erroDetalhe && <div className="ng-cartao"><p className="ng-explic">{erroDetalhe}</p></div>}
          {atual && !detalhe && !erroDetalhe && <div className="ng-cartao"><p className="ng-explic">Carregando…</p></div>}
          {detalhe && (
            <>
              <div className="ng-cartao">
                <div className="ng-cartao-cab">
                  <button type="button" className="ng-btn ng-voltar" onClick={() => navigate('/app/gestao/ambientes')}>‹ Ambientes</button>
                  <h2>{detalhe.nome} {!detalhe.ativo && <span className="ng-pill inativo">Inativo</span>}</h2>
                  <div className="ng-acoes">
                    {roles.includes('bi_environments_update') && <button type="button" className="ng-btn" onClick={() => { setNome(detalhe.nome); setErroJanela(null); setRenomear(true) }}>Renomear</button>}
                    {totalDash > 0 && detalhe.ativo && <Link className="ng-btn" to={`/app/dashboard/0/${detalhe.id}`}>Abrir no portal</Link>}
                    {(admin || roles.includes('bi_environments_update')) && <button type="button" className={`ng-btn${detalhe.ativo ? ' perigo' : ''}`} onClick={() => setInativar(true)}>{detalhe.ativo ? 'Inativar' : 'Reativar'}</button>}
                  </div>
                </div>
                <p className="ng-explic">{plural(totalDash, 'dashboard', 'dashboards')} · {plural(detalhe.grupos.length, 'grupo', 'grupos')} no menu</p>
                <div className="ng-campos" style={{ marginTop: 12 }}>
                  <label className="ng-campo"><span>Marca</span>
                    <select value={detalhe.marcaId || ''} onChange={e => trocarMarca(e.target.value)} disabled={!admin}>
                      <option value="">{TRADE_TALENTOS.nome} (padrão)</option>
                      {est.marcas.filter(m => m.id && m.nome.trim().toLowerCase() !== 'trade talentos').map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                    </select>
                    <small>{admin ? 'Cor, fonte e logo do portal quando alguém abre um dashboard deste ambiente.' : 'Só administradores do BI trocam a marca.'}</small>
                  </label>
                </div>
              </div>

              <div className="ng-cartao">
                <div className="ng-cartao-cab">
                  <h2>Dashboards</h2>
                  <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={abrirAdicionar}><AddLinkIcon />Adicionar dashboards</button></div>
                </div>
                <p className="ng-explic">Clique num dashboard para configurar regra, filtros, acessos e oneflow neste ambiente.</p>
                {totalDash > 0 && (
                  <div className="ng-filtros" style={{ marginBottom: 8 }}>
                    <label className="ng-busca"><SearchIcon fontSize="small" />
                      <input value={buscaDash} onChange={e => setBuscaDash(e.target.value)} placeholder="Buscar dashboard ou grupo" aria-label="Buscar dashboard ou grupo" />
                    </label>
                    <select className="ng-select" value={situacaoDash} onChange={e => setSituacaoDash(e.target.value)} aria-label="Situação">
                      <option value="ativo">Só ativos</option><option value="inativo">Só inativos</option><option value="">Ativos e inativos</option>
                    </select>
                  </div>
                )}
                {totalDash === 0 && <p className="ng-explic">Nenhum dashboard ainda. Use “Adicionar dashboards”.</p>}
                {totalDash > 0 && visiveisDash === 0 && <p className="ng-explic">Nenhum dashboard com esses filtros.</p>}
                {visiveisDash > 0 && (
                  <div className="ng-tabela">
                    {/* Uma tabela só, com largura fixa por coluna: as colunas ficam alinhadas entre os grupos. */}
                    <table className="ng-tabela-fixa">
                      <colgroup><col /><col style={{ width: '20%' }} /><col style={{ width: 80 }} /><col style={{ width: '18%' }} /><col style={{ width: 110 }} /></colgroup>
                      <thead><tr><th>Dashboard</th><th>Regra</th><th className="n">Filtros</th><th>Acesso</th><th>oneflow</th></tr></thead>
                      {grupos.filter(g => g.dashboards.length).map(g => (
                      <tbody key={g.id}>
                        <tr className="ng-grupo-linha"><td colSpan={5}>{g.nome} <small>{plural(g.dashboards.length, 'dashboard', 'dashboards')}</small></td></tr>
                        {g.dashboards.map(d => (
                          <tr key={d.id} className="clicavel" onClick={() => navigate(`/app/gestao/dashboards/${d.id}/ambiente/${detalhe.id}`)}>
                            <td><b>{d.nome}</b> {d.tipo === 'html' && <span className="ng-tag">HTML</span>} {!d.ativo && <span className="ng-pill inativo">Inativo</span>}</td>
                            <td>{d.regra || <span style={{ color: '#8b92a1' }}>Sem regra</span>}</td>
                            <td className="n">{d.filtros}</td>
                            <td>{d.equipes || d.pessoas
                              ? [d.equipes ? plural(d.equipes, 'equipe', 'equipes') : '', d.pessoas ? plural(d.pessoas, 'pessoa', 'pessoas') : ''].filter(Boolean).join(' · ')
                              : <span style={{ color: '#c4362b', fontWeight: 700 }}>Ninguém</span>}</td>
                            <td>{d.oneflow ? 'Ligado' : <span style={{ color: '#8b92a1' }}>Desligado</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                      ))}
                    </table>
                  </div>
                )}
              </div>

              <GruposDoMenu ambienteId={detalhe.id} grupos={detalhe.grupos} aoMudar={carregarDetalhe} />
            </>
          )}
        </section>
      </div>

      <Janela aberto={novo} titulo="Novo ambiente" sub="Depois você adiciona os dashboards e escolhe a marca." botao="Criar ambiente"
              desabilitado={nome.trim().length < 3} salvando={salvando} erro={erroJanela} aoConfirmar={criar} aoFechar={() => setNovo(false)}>
        <label className="nv-campo"><span>Nome</span><input value={nome} onChange={e => setNome(e.target.value)} maxLength={100} placeholder="Ex.: Cliente X · Nacional" /></label>
      </Janela>

      <Janela aberto={inativar} titulo={detalhe?.ativo ? 'Inativar ambiente' : 'Reativar ambiente'} botao={detalhe?.ativo ? 'Inativar' : 'Reativar'} perigo={!!detalhe?.ativo}
              salvando={salvando} aoConfirmar={confirmarAtivo} aoFechar={() => setInativar(false)}>
        <p style={{ margin: 0, lineHeight: 1.5 }}>{detalhe?.ativo
          ? <>Inativar <b>{detalhe?.nome}</b>? O ambiente some do menu, da busca e da página inicial de quem usa. Nada é apagado: dashboards, grupos, regras, filtros e acessos ficam guardados, e dá para reativar quando quiser.</>
          : <>Reativar <b>{detalhe?.nome}</b>? Ele volta a aparecer para quem tem acesso, como estava antes.</>}</p>
      </Janela>

      <Janela aberto={renomear} titulo="Renomear ambiente" botao="Salvar" desabilitado={nome.trim().length < 3 || nome.trim() === detalhe?.nome}
              salvando={salvando} erro={erroJanela} aoConfirmar={gravarNome} aoFechar={() => setRenomear(false)}>
        <label className="nv-campo"><span>Nome</span><input value={nome} onChange={e => setNome(e.target.value)} maxLength={100} /></label>
      </Janela>

      {detalhe && (
        <VinculoDialog
          aberto={adicionar}
          titulo="Adicionar dashboards"
          sub={`${detalhe.nome} · ${plural(totalDash, 'dashboard', 'dashboards')} hoje`}
          carregando={!catalogo}
          depois={(
            <label className="nv-campo"><span>Grupo no menu</span>
              <select value={grupoNovo} onChange={e => setGrupoNovo(e.target.value)}>
                <option value="">{detalhe.grupos.length ? 'Escolha o grupo' : 'Este ambiente ainda não tem grupo'}</option>
                {[...detalhe.grupos].sort((a, b) => a.ordem - b.ordem).map(g => <option key={g.id} value={g.nome}>{g.nome}</option>)}
              </select>
              <small>Para criar um grupo, use “Grupos do menu” nesta página.</small>
            </label>
          )}
          abas={[{
            id: 'dash', rotulo: 'Dashboards', dica: 'Buscar dashboard', unidade: ['dashboard', 'dashboards'], itens: itensCatalogo,
            filtros: [
              { id: 'powerbi', rotulo: 'Power BI', teste: (i: ItemVinculo) => i.tipo === 'powerbi' },
              { id: 'html', rotulo: 'Relatório HTML', teste: (i: ItemVinculo) => i.tipo === 'html' }
            ]
          }]}
          resumo={(sel, total) => total
            ? <><b>{plural(total, 'dashboard', 'dashboards')}</b> {total === 1 ? 'passa' : 'passam'} a aparecer em <b>{detalhe.nome}</b>, no grupo <b>{grupoNovo.trim() || '…'}</b>, <span className="alerta">sem ninguém com acesso ainda.</span> Depois, configure cada um.</>
            : <>Escolha os dashboards do catálogo que este ambiente vai mostrar.</>}
          botao={total => total ? `Adicionar ${plural(total, 'dashboard', 'dashboards')}` : 'Adicionar'}
          aoFechar={() => setAdicionar(false)}
          aoConfirmar={async sel => {
            if (!grupoNovo.trim()) return 'Escolha o grupo do menu.'
            const escolhidos = sel.dash || []
            for (const d of escolhidos) {
              const r = await services.api.novoPortalVincularDashboard(Number(d.id), { ambientes: [detalhe.id], grupo: grupoNovo.trim(), copiarDe: null })
              if (r.fail) { carregarDetalhe(); carregarLista(); return `${d.nome}: ${r.error || 'não foi possível adicionar.'}` }
            }
            setAdicionar(false)
            notify(`${plural(escolhidos.length, 'dashboard adicionado', 'dashboards adicionados')} a ${detalhe.nome}.`, { variant: 'success' })
            carregarDetalhe(); carregarLista()
            return null
          }}
        />
      )}
    </div>
  )
}
